// "MENU ITEMS SELECTION" PAGE
import {
   VUE_INSTANCE_DELIMITERS,
   CUSTOM_FIELD_HANDLE_NUMBER_OF_PEOPLE,
   CUSTOM_FIELD_HANDLE_DELIVERY_DATE,
} from '@/js/global/config';
import {MENUS_PRODUCT_TYPE, CARD_GATEWAY_ID} from '@/js/funnel/_config/commerce';
import {FUNNEL_VUE_INSTANCE_ID, MNY_VUE_COMPONENT_CONFIG, SCROLL_VUE_COMPONENT_CONFIG} from '@funnel/_config/vue';
import {debounce} from '@/js/global/helpers/requests';
import moment from 'moment-timezone';

const main = async () => {
   const [
      {default: Vue},
      {default: Vuex},
      {vuexStore},
      {DropdownPlugin, ModalPlugin, TooltipPlugin, CarouselPlugin, TabsPlugin, BCollapse, VBToggle},
      {ValidationProvider, ValidationObserver, extend, configure, localize},
      {required, min_value, max_value, digits, email, integer},
      {deliveryDate, emailTld},
      {default: ErrorMessages},
      {default: Datepicker},
      vueMny,
      VueScrollTo,
      {SelectionMixins},
      {AllergyIcons, CartValidationErrors, Feather, MenuFilter, MenuItemControls, MenuItemImage, MenuSection, OrderSummary},
   ] = await Promise.all([
      import(/* webpackChunkName: "vue" */ 'vue'),
      import(/* webpackChunkName: "vuex" */ 'vuex'),
      import(/* webpackChunkName: "funnel-vuex-store" */ '@/js/funnel/_store'),
      import(/* webpackChunkName: "bs-funnel-selection" */ 'bootstrap-vue'),
      import(/* webpackChunkName: "vee-validate" */ 'vee-validate'),
      import(/* webpackChunkName: "vee-validate-rules" */ 'vee-validate/dist/rules'),
      import(/* webpackChunkName: "global-validators" */ '@mymyValidators'),
      import(/* webpackChunkName: "global-translations-validation" */ '@mymyTranslations/validation'),
      import(/* webpackChunkName: "vue-datepicker" */ 'vuejs-datepicker'),
      import(/* webpackChunkName: "vue-mny" */ 'vue-mny'),
      import(/* webpackChunkName: "vue-scrollto" */ 'vue-scrollto'),
      import(/* webpackChunkName: "selection-mixins" */ '@/js/funnel/_mixins'),
      import(/* webpackChunkName: "selection-components" */ '@/js/funnel/_components'),
   ]);

   Vue.use(Vuex);
   Vue.use(DropdownPlugin);
   Vue.use(ModalPlugin);
   Vue.use(TooltipPlugin);
   Vue.use(CarouselPlugin);
   Vue.use(TabsPlugin);

   // for converting floats into currency in our template
   Vue.use(vueMny, MNY_VUE_COMPONENT_CONFIG);

   // for scrolling the customer around the page.
   // search for "$scrollTo" in this file to see how to use it.
   Vue.use(VueScrollTo, SCROLL_VUE_COMPONENT_CONFIG);

   // load our translated form error messages for this page's language
   const errorMessages = ErrorMessages[window.mymy.request.currentSite.language];
   localize(window.mymy.request.currentSite.language, errorMessages);

   // add our Vee Validate validators
   extend('required', required);
   extend('min_value', min_value);
   extend('max_value', max_value);
   extend('digits', digits);
   extend('deliveryDate', deliveryDate);
   extend('email', email);
   extend('emailTld', emailTld);

   // customize these error messages
   extend('min_value', {message: errorMessages.messages.acMinPeople});
   extend('max_value', {message: errorMessages.messages.acMaxPeople});

   // tell Vee Validate which classes to apply when a field is valid/invalid, etc
   configure({classes: {invalid: 'is-invalid'}});

   // use these local components as plugins since they are used in multiple components
   AllergyIcons.install = (Vue) => Vue.component(AllergyIcons.name, AllergyIcons);
   Vue.use(AllergyIcons);

   // Create our vue instance
   const vm = new Vue({
      name: 'MenuItemsSelectionPage',
      el: FUNNEL_VUE_INSTANCE_ID,
      delimiters: VUE_INSTANCE_DELIMITERS,
      store: vuexStore(),
      mixins: SelectionMixins,
      directives: {'b-toggle': VBToggle},
      components: {
         'allergy-icons': AllergyIcons,
         'b-collapse': BCollapse,
         'cart-validation-errors': CartValidationErrors,
         'datepicker': Datepicker,
         'feather-icon': Feather,
         'menu-filter': MenuFilter,
         'menu-item-image': MenuItemImage,
         'menu-item-controls': MenuItemControls,
         'menu-section': MenuSection,
         'order-summary': OrderSummary,
         'validation': ValidationProvider,
         'validation-observer': ValidationObserver,
      },
      mounted() {
         // listen to hash change event
         // Should be compatible with all kind of browsers
         window.onhashchange = () => {
            this.scrollToTop();

            if (window.posthog) {
               // manually capture pageviews since we are in an SPA like env
               window.posthog.capture('$pageview')
            }

            this._goToSectionOnLoad();
         };

         // fire off an event to GTM that this menu's details page is being viewed.
         this.gtmViewMenuDetails();

         this.$nextTick(() => {
            this._goToSectionOnLoad();

            // load the items the viewer sees into the `dataLayer` for GTM. This happens after
            // `goToSectionOnLoad()` sets the `this.$store.state.menu.sectionIndex`.
            this.gtmViewItemsInCurrentSection();
         });
      },
      created() {
         /**
          * Delay updating the cookie when custom form fields change.
          */
         this.debounceCookieSyncWithFields = debounce(() => this.updateMenuCookieWithFields(), 2000);

         // Adds a browser modal asking if you want to leave the page or not
         // - https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
         window.onbeforeunload = (event) => {
            // Cancel the event. preventDefault() is preferred but returnValue = '' is needed
            // for Chrome.
            event.preventDefault();
            event.returnValue = '';
         };
      },
      watch: {
         /**
          * Load the items the viewer sees into the `dataLayer` every time a section changes.
          */
         sectionIndex() {
            this.gtmViewItemsInCurrentSection();
         },

         /**
          * When the local 'fields' object updates because the customer is interacting with the
          * form fields above the summary, update their menu cookie and cart data in the Vuex store.
          */
         fields: {
            deep: true,
            handler(fields) {
               // keep the cart's 'numberOfPeople' synced with the local form field data
               this.$store.commit('updateCustomField', {
                  field: CUSTOM_FIELD_HANDLE_NUMBER_OF_PEOPLE,
                  value: fields.numberOfPeople
               });

               // keep the cart's 'deliveryDate' synced with the local form field data
               this.$store.commit('updateCustomField', {
                  field: CUSTOM_FIELD_HANDLE_DELIVERY_DATE,
                  value: fields.delivery.date
               });

               // update the cookie
               if (this.isReady) {
                  this.debounceCookieSyncWithFields();
               }
            },
         }
      },

      methods: {
         /**
          * Update the local 'fields' data with menu cookie data.
          *
          * @param menuCookie {object}
          */
         updateFieldsWithMenuCookie(menuCookie) {
            let deliveryDate = menuCookie.d ?? menuCookie.deliveryDate ?? null;
            if (deliveryDate) {
               this.fields.delivery.date = moment(deliveryDate).toDate();
            }

            let numberOfPeople = menuCookie.q ?? menuCookie.numberOfPeople ?? null;
            if (typeof numberOfPeople === 'number' && numberOfPeople) {
               this.fields.numberOfPeople = numberOfPeople;
            }

            let postalCode = menuCookie.p ?? menuCookie.postalCode ?? null;
            if (typeof postalCode === 'string' && postalCode) {
               this.fields.postalCode = postalCode;
            }
         },

         /**
          * Update the menu cookie with local 'fields' data. This method also returns the cookie
          * object.
          *
          * @return {object}
          */
         updateMenuCookieWithFields() {
            return this.setMenuCookie({
               deliveryDate: this.fields.delivery.date,
               numberOfPeople: this.fields.numberOfPeople,
               postalCode: this.fields.postalCode,
            });
         },

         /**
          * Check if our hidden "honey pot" field has a value. If it does, then a bot has filled
          * in the field, thus failing the test.
          *
          * @returns {boolean}
          */
         passesHoneyPotTest() {
            return this.$refs.firstName.value === '';
         },

         showMobileSummary() {
            this.modals.mobileSummary.show = true;
         },

         /**
          * When the customer is on DESKTOP and submits their selection to go to the next page,
          * validate their "Get Started" form fields and (if valid) update their cart.
          *
          * @return {Promise<void>}
          * @see updateCart
          */
         async updateCartDesktop() {
            if (this.passesHoneyPotTest()) {
               await this.onValidate('observer-get-started-form-desktop', this.updateCart);
            }
         },

         /**
          * When the customer is on MOBILE and submits their selection to go to the next page,
          * validate their "Get Started" form fields and (if valid) update their cart.
          *
          * @return {Promise<void>}
          * @see updateCart
          */
         async updateCartMobile() {
            if (this.passesHoneyPotTest()) {
               await this.onValidate('observer-get-started-form-mobile', this.updateCart);
            }
         },

         /**
          * Collect the data and post it to Commerce's controller.
          *
          * This is the final method called when the customer has reached the last step, all
          * requirements have been met, and they click the button to go to the next step.
          * Upon success, they will be redirected to the Delivery page.
          *
          * @see updateCartDesktop
          * @see updateCartMobile
          */
         async updateCart() {
            this.closeAllModals();
            this.modals.loading.show = true;

            // get a new cart number if the customer doesn't have one yet for this menu
            if (! this.cart.number) {
               const newCart = await this.$store.dispatch('getNewCartNumber');
               this.$store.commit('updateCartData', newCart.data);
               if (this.cart.number) {
                  this.setMenuCookie({ cartNumber: this.cart.number });
               }
            }

            // without a cart number in the store data, we can't go any further
            if (! this.cart.number) {
               this.showErrorModal(this.getDefaultErrorMessage(this.cart.shortNumber ?? '{null}'));
               throw new Error("A cart number doesn't exist.");
               return;
            }

            // keep the cookie up to date
            this.updateMenuCookieWithFields();

            let data = {
               fields: {
                  deliveryDate: this.getDeliveryDateForCraft(this.fields.delivery),
                  numberOfPeople: this.fields.numberOfPeople,
                  urlQueryParameters: this._prepareUrlQueryParamsForSaving(),
                  postalCode: this.fields.postalCode,
               },
               redirect: this.nextPageUrl,
            };

            // set the "cart" gateway as the default
            if (typeof this.cart.gatewayId !== 'number') {
               data['gatewayId'] = CARD_GATEWAY_ID;
            }

            // get the line items for the cart
            let items = this._getSelectedItemsForPost();
            if (items.purchasables.length) {
               data['purchasables'] = items.purchasables;
            }
            if (Object.keys(items.lineItems).length) {
               data['lineItems'] = items.lineItems;
            }

            // if this is a package menu, add the selected course dishes
            let menuCoursesSnapshot = this._getMenuCoursesSnapshotFieldData();
            if (Object.keys(menuCoursesSnapshot).length) {
               data.fields['menuCoursesSnapshot'] = menuCoursesSnapshot;
            }

            // post to Commerce's controller
            const response = await this.$store.dispatch('updateCart', data);

            console.log(response)
            if (response?.data?.cart && response?.data?.redirect) {
               this.setMenuCookie({cartNumber: response.data.cart.number});
               this.redirectUser(response.data.redirect);
            }
            else if (response?.data?.error) {
               this.showErrorModal(this.getYiiErrors(response.data.error));
            }
            else if (response?.data?.errors) {
               this.showErrorModal(this.getYiiErrors(response.data.errors));
            }
            else {
               console.log(response)
               this.showErrorModal(this.getDefaultErrorMessage(response?.data?.cart?.shortNumber));
               if(window.Sentry) {
                  Sentry.captureMessage("Response not handled correctly")
               }
            }
         },

         /**
          * Collect all query params to save on the cart. This will exclude special query params
          * used for making the funnel operate. Typical params to be collected here might be UTM
          * params for Marketing.
          *
          * @return {*[]}
          * @private
          */
         _prepareUrlQueryParamsForSaving() {

            // any params already saved on the cart + anything currently in the URL +
            // automatically stripping out reserved params
            const urlParams = this.getQueryParamsForSaving();

            // 'rows' refers to Craft's "table" custom field type
            let rows = [];

            // next add in any params from the current URL that aren't reserved or already on
            // the cart
            urlParams.forEach((value, key) => {
               rows.push({
                  col1: key,
                  col2: value,
                  name: key,
                  value: value,
               });
            });

            return rows;
         },

         /**
          * Figure out which line items are new, which have edited quantities, and which should
          * be removed from the cart.
          *
          * @return {{lineItems: {}, purchasables: *[]}}
          * @private
          */
         _getSelectedItemsForPost() {
            let current = {
               purchasables: [], // selected MenuItemsControls that are new in the cart
               lineItems: {}, // edits to existing MenuItemsControls already on the cart
            };

            let OrderSummary = this.getSummaryComponent();

            // get the line items from the summary (but not organized by product type)
            let currentLineItems = OrderSummary.getLineItemsList(false);

            // the line items that already existed on the cart before loading the Selection page
            const staticLineItems = OrderSummary.getLineItemsList(true);

            console.log('_getSelectedItemsForPost:STATIC', staticLineItems);
            console.log('_getSelectedItemsForPost:CURRENT', currentLineItems);


            // first, let's remove any line items from the cart that no longer exist in the order
            // summary, meaning that the customer removed an item they once added to the cart on
            // a previous visit to the Selection page.
            for (let slug in staticLineItems) {
               // we're looking for static line items that are no longer in the summary
               if (currentLineItems[slug] ?? null) {
                  continue;
               }

               const staticLineItem = staticLineItems[slug] ?? null;
               if (staticLineItem) {
                  // tell Commerce's controller to remove this line item completely
                  current.lineItems[staticLineItem.lineItemId] = {
                     remove: true
                  };

                  // add this item's GTM `productFieldObject` to our local data object so we
                  // can fire a `eec.removeFromCart` gtm event after successfully updating the
                  // cart.
                  let MenuItemControls = this.getMenuItemComponent(staticLineItem.slug);
                  if (MenuItemControls) {
                     this.gtmRemoveItemFromCart(
                        MenuItemControls.gtmItem(),
                        MenuItemControls.price
                     );
                  }
               }
            }


            // next, loop through line items that are dishes, drinks, and accessories and add or
            // edit them in the cart
            for (let menuItemSlug in currentLineItems) {
               let summaryItemObject = currentLineItems[menuItemSlug];
               const staticLineItem = staticLineItems[menuItemSlug] ?? null;

               let MenuItemControls = this.getMenuItemComponent(menuItemSlug);


               /**
                * PURCHASABLES
                *  - These are newly added line items to the cart
                *//////////////////////////////////////////////////////////////////////////////
               if (staticLineItem === null) {
                  current.purchasables.push({
                     id: summaryItemObject.variantId,
                     qty: summaryItemObject.quantity,
                  });

                  // add this item's GTM `productFieldObject` to our local data object so we
                  // can fire a `eec.addToCart` gtm event after successfully updating the cart.
                  if (MenuItemControls) {
                     this.gtmAddItemToCart(
                        MenuItemControls.gtmItem({
                           quantity: summaryItemObject.quantity
                        }),
                        MenuItemControls.priceAsFloat
                     );
                  }

                  continue;
               }


               /**
                * LINE ITEMS
                *  - Update the quantities of any existing cart items
                *//////////////////////////////////////////////////////////////////////////////

               // if no change in quantity, nothing to report to Commerce's controller
               if (summaryItemObject.quantity === staticLineItem.quantity) {
                  continue
               }

               // tell Commerce's controller to update this line item's quantity
               current.lineItems[staticLineItem.lineItemId] = {
                  qty: summaryItemObject.quantity
               };


               // add or remove this item's GTM `productFieldObject` to our local data
               // object so we can fire a gtm event after successfully updating the
               // cart.
               if (!MenuItemControls) {
                  continue;
               }
               if (summaryItemObject.quantity > staticLineItem.quantity) {
                  // if the existing cart item's quantity increased, add the
                  // quantity that was added.
                  this.gtmAddItemToCart(
                     MenuItemControls.gtmItem({
                        quantity: (summaryItemObject.quantity - staticLineItem.quantity)
                     }),
                     MenuItemControls.priceAsFloat
                  );
               }
               else {
                  // if the existing cart item's quantity decreased, remove the
                  // quantity that was subtracted.
                  this.gtmRemoveItemFromCart(
                     MenuItemControls.gtmItem({
                        quantity: (staticLineItem.quantity - summaryItemObject.quantity)
                     }),
                     MenuItemControls.priceAsFloat
                  );
               }
            }

            console.log('_getSelectedItemsForPost:RETURN', current);

            return current;
         },

         /**
          * Take all the course dishes the customer selected and format it for our
          * `menuCoursesSnapshot` matrix field.
          *
          * This only applies if the menu is traditional and not a-la-carte.
          *
          * @returns {*}
          * @private
          */
         _getMenuCoursesSnapshotFieldData() {
            if (this.menu.isALaCarte) {
               return {};
            }

            // first let's detect if the customer changed any course items since they last
            // visited the Selection page
            let OrderSummary = this.getSummaryComponent();
            const staticCourseItems = OrderSummary.getCourseItemsList(true);
            let currentCourseItems = OrderSummary.getCourseItemsList(false);
            let differences = 0;
            for (let slug in currentCourseItems) {
               if (staticCourseItems[slug] ?? null) {
                  continue;
               }
               ++ differences;
            }
            if (differences === 0) {
               return {};
            }


            // record the course dishes that are selected so we can deduce if anything has
            // been removed from the cart and report it to GTM (see `for` loop towards
            // bottom of this method)
            let selectedMenuItemSlugs = [];

            let menuCoursesSnapshot = {};
            let index = 1;
            for (let MenuSection of this.menu.sections) {
               // skip sections that aren't courses
               if (!MenuSection.isForCourseDishes) {
                  continue;
               }

               // get all the selected dishes (MenuItemControls.vue component) for this section.
               if (MenuSection.totalSelectedItems < 1) {
                  continue;
               }

               // add the dishes formatted for a Craft table field
               let dishTableRows = [];
               for (let key in MenuSection.selectedItems) {
                  let MenuItemControls = this.getMenuItemComponent(key);
                  dishTableRows.push({
                     col1: MenuItemControls.variant.title,
                     col2: MenuItemControls.variant.sku,
                     col3: MenuItemControls.variant.id,
                  });

                  selectedMenuItemSlugs.push(MenuItemControls.id);

                  // if this dish doesn't exist in the initial course dishes
                  // already on the cart, then it's newly added and we need to add this item's
                  // GTM `productFieldObject` to our local data object so we can fire a
                  // `eec.addToCart` gtm event after successfully updating the cart.
                  if (MenuItemControls.isInitialCourseItem()) {
                     this.gtmAddItemToCart(
                        MenuItemControls.gtmItem({
                           quantity: this.cart.numberOfPeople
                        }),
                        MenuItemControls.priceAsFloat
                     );
                  }
               }

               // set up a matrix block for the `menuCoursesSnapshot` field
               menuCoursesSnapshot['new' + index] = {
                  type: 'course',
                  fields: {
                     courseTitle: MenuSection.title,
                     relatedCourseCategory: [MenuSection.courseCategory.id],
                     dishVariants: dishTableRows,
                  },
               };

               ++ index;
            }

            // now lets find the course dishes that were removed based on what was in the
            // cart already so we can add this item's GTM `productFieldObject` to our local data
            // object and fire a `eec.removeFromCart` gtm event after successfully updating
            // the cart.
            for (let courseCategorySlug in window.mymy.cart.summary.courseItems) {
               for (let menuItemSlug in window.mymy.cart.summary.courseItems[courseCategorySlug]) {
                  if (!selectedMenuItemSlugs.includes(menuItemSlug)) {
                     let MenuItemControls = this.getMenuItemComponent(menuItemSlug);
                     if (MenuItemControls) {
                        this.gtmRemoveItemFromCart(
                           MenuItemControls.gtmItem({
                              quantity: this.summary.lineItems[MENUS_PRODUCT_TYPE].quantity
                           }),
                           MenuItemControls.priceAsFloat
                        );
                     }
                  }
               }
            }

            return menuCoursesSnapshot;
         },
      },
   });

   return vm;
};

// Execute async function
main().then((vm) => {
});

// Accept HMR as per: https://webpack.js.org/api/hot-module-replacement#accept
if (module.hot) {
   module.hot.accept();
}